import { assertUnreachable } from '@evorait/pace-core';
import { DatePerformed } from '../types/date-performed';
import { DefectiveComponentReplacement } from '../types/defective-component-replacement';
import { DiscrepancyLog } from '../types/discrepancy-log';
import { FormsConstants } from '../types/forms-constants';
import { FunctionalElement, FUNCTIONAL_ELEMENT_TYPE } from '../types/functional-element';
import { MaterialSerialNumber } from '../types/material-serial-number';
import { Option } from '../types/option';
import { ReplacementOrLastProcedureDate } from '../types/replacement-or-last-procedure-date';
import { Signature } from '../types/signature';
import { StandardFunctionalElement } from '../types/standard-functional-element';
import { ValueFunctionalElement } from '../types/value-functional-element';
import { DateUtilities } from './date-utilities';

function getStandardFunctionalElementXmlIds(s: StandardFunctionalElement): Array<{ xmlId: string; value: string }> {
    return [{ xmlId: s.remarksXmlId ?? '', value: s.remarks?.length ? s.remarks : FormsConstants.NA }];
}

function getDiscrepancyLogXmlIds(d: DiscrepancyLog): Array<{ xmlId: string; value: string }> {
    return [{ xmlId: d.xmlId, value: d.value }];
}

function getMaterialSerialNumberXmlIds(m: MaterialSerialNumber): Array<{ xmlId: string; value: string }> {
    return [
        { xmlId: m.materialXmlId, value: m.materialValue?.length > 0 ? m.materialValue : '' },
        { xmlId: m.serialXmlId, value: m.serialValue?.length > 0 ? m.serialValue : '' },
    ];
}

function getMaterialSerialNumberWithDescXmlIds(m: MaterialSerialNumber): Array<{ xmlId: string; value: string }> {
    return [
        { xmlId: m.materialXmlId, value: m.materialValue?.length > 0 ? m.materialValue : '' },
        { xmlId: m.serialXmlId, value: m.serialValue?.length > 0 ? m.serialValue : '' },
        { xmlId: m.descriptionXmlId, value: m.descriptionValue?.length > 0 ? m.descriptionValue : '' },
    ];
}

function getSignatureXmlIds(s: Signature): Array<{ xmlId: string; value: string }> {
    return [
        {
            xmlId: s.nameXmlId,
            value: s.firstName.length > 0 || s.lastName.length > 0 ? `${s.firstName} ${s.lastName}` : '',
        },
        { xmlId: s.dateXmlId, value: s.date.length > 0 ? s.date : '' },
    ];
}

function getDatePerformedXmlIds(d: DatePerformed): Array<{ xmlId?: string; value: string }> {
    return [{ xmlId: d.yearXmlId, value: FormsConstants.NA }];
}

function getDateXmlIds(d: DatePerformed): Array<{ xmlId?: string; value: string }> {
    return [
        { xmlId: d.dayXmlId, value: FormsConstants.NA },
        { xmlId: d.monthXmlId, value: FormsConstants.NA },
        { xmlId: d.yearXmlId, value: FormsConstants.NA },
    ];
}

function getDefectiveComponentReplacementXmlIds(
    d: DefectiveComponentReplacement,
): Array<{ xmlId: string; value: string }> {
    return [
        { xmlId: d.repairTimeXmlId, value: FormsConstants.NA },
        { xmlId: d.swapOutComponent.matNoXmlId, value: FormsConstants.NA },
        { xmlId: d.swapOutComponent.serialNoXmlId, value: FormsConstants.NA },
        { xmlId: d.swapInComponent.matNoXmlId, value: FormsConstants.NA },
        { xmlId: d.swapInComponent.serialNoXmlId, value: FormsConstants.NA },
    ];
}

function getValueXmlIds(v: ValueFunctionalElement): Array<{ xmlId: string; value: string }> {
    return [{ xmlId: v.xmlId, value: v.value?.length > 0 ? v.value : '' }];
}

// functional element only available in astoria.
function getReplacementOrLastPerformedDateXmlIds(
    r: ReplacementOrLastProcedureDate,
): Array<{ xmlId: string; value: string }> {
    const { day, month, year } = DateUtilities.getDayMonthYearValuesFromDateString(r.date);

    return [
        { xmlId: r.dayXmlId, value: day },
        { xmlId: r.monthXmlId, value: month },
        { xmlId: r.yearXmlId, value: year },
        { xmlId: r.radioButtonXmlId, value: '' },
    ];
}

function getOptionXmlIds(o: Option): Array<{ xmlId: string; value: string }> {
    return [{ xmlId: o.xmlId, value: '' }];
}

function getWorkstepsPerformedXmlIds(): Array<{ xmlId: string; value: string }> {
    // WORKSTEPS_PERFORMED is not rendered and removed from XML when submitting - nothing to return here
    return [];
}

export function getXmlIdsFromFunctionalElement(f: FunctionalElement): Array<{ xmlId?: string; value: string }> {
    switch (f.type) {
        case FUNCTIONAL_ELEMENT_TYPE.DATE:
            return getDateXmlIds(f as DatePerformed);
        case FUNCTIONAL_ELEMENT_TYPE.STANDARD:
        case FUNCTIONAL_ELEMENT_TYPE.REMARK:
        case FUNCTIONAL_ELEMENT_TYPE.DROP_DOWN_OPTIONS:
            return getStandardFunctionalElementXmlIds(f as StandardFunctionalElement);
        case FUNCTIONAL_ELEMENT_TYPE.DISCREPANCY_LOG:
            return getDiscrepancyLogXmlIds(f as DiscrepancyLog);
        case FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE:
        case FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE:
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE:
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER:
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION:
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER_SYSTEM_CONDITION:
            return getSignatureXmlIds(f as Signature);
        case FUNCTIONAL_ELEMENT_TYPE.COMPONENT:
        case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO:
            return getMaterialSerialNumberXmlIds(f as MaterialSerialNumber);
        case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC:
            return getMaterialSerialNumberWithDescXmlIds(f as MaterialSerialNumber);
        case FUNCTIONAL_ELEMENT_TYPE.DATE_PERFORMED:
            return getDatePerformedXmlIds(f as DatePerformed);
        case FUNCTIONAL_ELEMENT_TYPE.VALUE:
        case FUNCTIONAL_ELEMENT_TYPE.VALUE_UNIT:
            return getValueXmlIds(f as ValueFunctionalElement);
        case FUNCTIONAL_ELEMENT_TYPE.REPLACEMENT_DATE:
        case FUNCTIONAL_ELEMENT_TYPE.LAST_PROCEDURE_DATE:
            return getReplacementOrLastPerformedDateXmlIds(f as ReplacementOrLastProcedureDate);
        case FUNCTIONAL_ELEMENT_TYPE.OPTION:
        case FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT:
            return getOptionXmlIds(f as Option);
        case FUNCTIONAL_ELEMENT_TYPE.WORKSTEPS_PERFORMED:
            return getWorkstepsPerformedXmlIds();
        case FUNCTIONAL_ELEMENT_TYPE.DEFECTIVE_COMPONENT_REPLACEMENT:
            return getDefectiveComponentReplacementXmlIds(f as DefectiveComponentReplacement);
        default:
            assertUnreachable(f.type);
    }
}
